/**
 * The pure half of the shortcut layer: turning key events into strokes and
 * strokes into bindings. No DOM and no React, so it is tested directly; the
 * provider in components/shell/commands.tsx wires it to the document.
 *
 * A binding is written "g i" (a sequence: G, then I), "mod+k" (a chord), or a
 * single key such as "c" or "?". `mod` is Command on Apple platforms and
 * Control everywhere else.
 */

export const SEQUENCE_TIMEOUT_MS = 1000

export function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent)
}

type KeyEventLike = Pick<
  KeyboardEvent,
  "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey"
> & { code?: string }

const MODIFIER_KEYS = new Set(["Shift", "Meta", "Control", "Alt", "AltGraph", "CapsLock"])

/**
 * Normalises a key event into a stroke such as "g", "mod+k" or "shift+j".
 * Returns null for a modifier pressed on its own.
 */
export function strokeFromEvent(event: KeyEventLike, apple: boolean): string | null {
  if (MODIFIER_KEYS.has(event.key)) return null
  // The digit keys of the top row are named by where they are, not by what
  // they type. On a French keyboard that row types & é " ' ( and gives the
  // digits only with Shift, and "press 1" has to be the same key on both.
  const digit = /^Digit([0-9])$/.exec(event.code ?? "")?.[1]
  const key = digit ?? (event.key === " " ? "space" : event.key.toLowerCase())
  const parts: string[] = []
  const primary = apple ? event.metaKey : event.ctrlKey
  const secondary = apple ? event.ctrlKey : event.metaKey
  if (primary) parts.push("mod")
  if (secondary) parts.push("ctrl")
  if (event.altKey) parts.push("alt")
  // Shift is already part of a symbol ("?" is Shift+/) and, depending on the
  // keyboard, of a digit, so it only counts as a modifier next to letters
  // and named keys.
  const symbol = key.length === 1 && !/[a-z]/.test(key)
  if (event.shiftKey && !symbol) parts.push("shift")
  parts.push(key)
  return parts.join("+")
}

/** "g i" gives ["g", "i"]; "mod+k" gives ["mod+k"]. */
export function parseBinding(keys: string): string[] {
  return keys.trim().toLowerCase().split(/\s+/).filter(Boolean)
}

/** Whether a stroke carries the platform's command modifier. */
export function hasModifier(stroke: string): boolean {
  return stroke.startsWith("mod+") || stroke.includes("+mod+")
}

export type SequenceResult =
  | { status: "matched"; id: string }
  | { status: "pending" }
  | { status: "none" }

/**
 * Compares the strokes typed so far with every binding. `pending` means the
 * strokes are the start of at least one longer binding, so the caller keeps
 * them and waits for the next key.
 */
export function resolveSequence(
  typed: readonly string[],
  bindings: ReadonlyMap<string, readonly string[]>,
): SequenceResult {
  let pending = false
  for (const [id, binding] of bindings) {
    if (binding.length < typed.length) continue
    if (!typed.every((stroke, index) => binding[index] === stroke)) continue
    if (binding.length === typed.length) return { status: "matched", id }
    pending = true
  }
  return pending ? { status: "pending" } : { status: "none" }
}

const KEY_LABELS: Record<string, { apple: string; other: string }> = {
  mod: { apple: "⌘", other: "Ctrl" },
  ctrl: { apple: "⌃", other: "Win" },
  alt: { apple: "⌥", other: "Alt" },
  shift: { apple: "⇧", other: "Shift" },
  enter: { apple: "↵", other: "↵" },
  escape: { apple: "Esc", other: "Esc" },
  backspace: { apple: "⌫", other: "⌫" },
  arrowup: { apple: "↑", other: "↑" },
  arrowdown: { apple: "↓", other: "↓" },
  arrowleft: { apple: "←", other: "←" },
  arrowright: { apple: "→", other: "→" },
  space: { apple: "Space", other: "Space" },
}

/** The label printed on a key cap. */
export function displayKey(key: string, apple: boolean = isApplePlatform()): string {
  const label = KEY_LABELS[key]
  if (label) return apple ? label.apple : label.other
  return key.length === 1 ? key.toUpperCase() : key
}

/** True for elements where a plain key press is text, not a shortcut. */
export function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    return true
  }
  if (target instanceof HTMLInputElement) {
    return !["checkbox", "radio", "button", "submit", "reset"].includes(target.type)
  }
  return false
}
